import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { trackOperation } from './trackOperation';

export function useTrackedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  operationName?: string,
): UseMutationResult<TData, TError, TVariables, TContext> {
  // Determine mutation name from multiple sources (priority order)
  let mutationName: string;

  if (operationName) {
    // 1. Explicit operationName parameter (highest priority)
    mutationName = operationName;
  } else if (options?.mutationKey) {
    // 2. Use the mutationKey if available
    mutationName = Array.isArray(options.mutationKey)
      ? options.mutationKey.join(':')
      : String(options.mutationKey);
  } else {
    // 3. Fallback to generic name with warning
    mutationName = 'unnamed';
    console.warn(
      'useTrackedMutation: No operationName or mutationKey provided. ' +
        'Please add an operationName parameter or mutationKey for better Sentry tracking. ' +
        'Example: useTrackedMutation(options, "createTarget") or mutationKey: ["targets", "create"]',
    );
  }

  // Extract service from operation name (e.g., 'shape:create' -> 'shape')
  const service = mutationName.includes(':') 
    ? mutationName.split(':')[0] 
    : 'unknown';

  // Extract operation action from operation name (e.g., 'shape:create' -> 'create')
  const operationAction = mutationName.includes(':') 
    ? mutationName.split(':').slice(1).join(':') 
    : 'unknown';

  // Wrap the mutationFn with our generic Sentry span tracker
  const trackedMutationFn = options.mutationFn
    ? trackOperation(
        options.mutationFn,
        `mutation:${mutationName}`,
        'mutation',
        {
          mutation_key: Array.isArray(options.mutationKey)
            ? options.mutationKey.join(':')
            : String(options.mutationKey || 'none'),
          service: service,
          operation_action: operationAction,
        },
      )
    : undefined;

  // Use the original useMutation with our wrapped function (React Query v5 API)
  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: trackedMutationFn,
  });
}
